/**
 * Snapshot of the coaching readiness fields the pre-dial gate polls. Read
 * through a getter (typically a ref) so the poll loop always sees fresh
 * values without re-creating the dial handlers — mirrors the legacy
 * `CallButton`'s `coachingRef` pattern.
 */
export type CoachingReadinessSnapshot = {
	conversationId: string | null;
	isResponding: boolean;
	isCreatingConversation: boolean;
};

const DEFAULT_MAX_ATTEMPTS = 15;
const DEFAULT_INTERVAL_MS = 1000;

const sleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

/**
 * Wait for the coaching conversation to be ready before dialing, so the
 * outbound `POST phonecall` carries `aiConversationId` and the server sets the
 * reverse `PhoneCall.AIConversationId` link at creation. Without that link the
 * post-call adherence check silently skips the call and "See AI Feedback"
 * never appears on the transcript.
 *
 * Mirrors the legacy queue's `startCoachingBeforeCall`
 * (`aida/voice-agent/utils/startCoachingBeforeCall.ts`) byte-for-byte in
 * behavior:
 *   - Fast path: a conversation already exists and isn't mid-create → return
 *     it immediately (legacy `alreadyReady`; does NOT wait on `isResponding`).
 *   - Otherwise poll every second, up to 15 attempts, for
 *     `conversationId && !isResponding && !isCreatingConversation`.
 *   - On timeout, warn and return whatever id exists at that moment (possibly
 *     null) — the call must proceed even if coaching never initialized.
 *
 * The focused view's `useCoaching` auto-bootstraps the conversation when the
 * lead lands, so unlike legacy there is no `startCoaching()` kick here — this
 * gate only waits for that in-flight bootstrap to settle.
 */
export const waitForCoachingConversation = async (
	getSnapshot: () => CoachingReadinessSnapshot,
	options?: { maxAttempts?: number; intervalMs?: number }
): Promise<string | null> => {
	const maxAttempts = options?.maxAttempts ?? DEFAULT_MAX_ATTEMPTS;
	const intervalMs = options?.intervalMs ?? DEFAULT_INTERVAL_MS;

	// Legacy `alreadyReady` fast path: dial immediately when a conversation is
	// settled, even if the AI is mid-response (e.g. the Game Plan is streaming).
	const initial = getSnapshot();
	if (initial.conversationId && !initial.isCreatingConversation) {
		return initial.conversationId;
	}

	for (let attempt = 0; attempt < maxAttempts; attempt++) {
		await sleep(intervalMs);
		const snapshot = getSnapshot();
		if (snapshot.conversationId && !snapshot.isResponding && !snapshot.isCreatingConversation) {
			return snapshot.conversationId;
		}
	}

	console.warn('waitForCoachingConversation: Timeout waiting for coaching to initialize');
	return getSnapshot().conversationId;
};
